import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Honeypot + timing fields shared by both paths. */
const antiSpam = {
  /** Honeypot: must stay empty. Bots that fill every field get rejected. */
  website: z.string().max(200).optional(),
  /** Client timestamp (ms) of when the form was rendered, used to reject instant bot submits. */
  startedAt: z.number().optional(),
};

export const PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
] as const;

export const CHALLENGES = [
  "Improving pass rates",
  "Closing learning gaps",
  "Exam readiness",
  "Learner engagement",
  "Other",
] as const;

const schoolApplicationSchema = z.object({
  kind: z.literal("school"),
  schoolName: z.string().trim().min(1, "School name is required").max(200),
  schoolLocation: z.string().trim().min(1, "School location is required").max(200),
  matricLearnerCount: z.string().trim().min(1, "This field is required").max(50),
  biggestChallenge: z.enum(CHALLENGES),
  biggestChallengeOther: z.string().trim().max(300).optional(),
  fullName: z.string().trim().min(1, "Name is required").max(100, "Name is too long"),
  position: z.string().trim().min(1, "Position is required").max(150),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email is too long"),
  phone: z.string().trim().min(1, "Contact number is required").max(50),
  message: z.string().trim().max(1000).optional(),
  consent: z.literal(true, { message: "Please confirm the consent statement" }),
  ...antiSpam,
});

const nominationSchema = z.object({
  kind: z.literal("nomination"),
  role: z.enum(["learner", "parent"]),
  fullName: z.string().trim().min(1, "Name is required").max(100, "Name is too long"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email is too long"),
  schoolName: z.string().trim().min(1, "School name is required").max(200),
  province: z.enum(PROVINCES),
  phone: z.string().trim().max(50).optional(),
  message: z.string().trim().max(1000).optional(),
  ...antiSpam,
});

export const GRADES = ["10", "11", "12", "Other"] as const;

const individualSchema = z.object({
  kind: z.literal("individual"),
  role: z.enum(["learner", "parent"]),
  fullName: z.string().trim().min(1, "Name is required").max(100, "Name is too long"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email is too long"),
  grade: z.enum(GRADES),
  province: z.enum(PROVINCES),
  phone: z.string().trim().max(50).optional(),
  message: z.string().trim().max(1000).optional(),
  consent: z.literal(true, { message: "Please confirm the consent statement" }),
  ...antiSpam,
});

const waitlistSchema = z.discriminatedUnion("kind", [
  schoolApplicationSchema,
  nominationSchema,
  individualSchema,
]);

export type WaitlistInput = z.infer<typeof waitlistSchema>;

function createSupabaseFetch(supabaseKey: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined,
    );
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    // New Supabase publishable keys are opaque strings, not bearer JWTs.
    if (headers.get("Authorization") === `Bearer ${supabaseKey}`) {
      headers.delete("Authorization");
    }
    headers.set("apikey", supabaseKey);
    return fetch(input, { ...init, headers });
  };
}

export const submitWaitlist = createServerFn({ method: "POST" })
  .inputValidator((data) => waitlistSchema.parse(data))
  .handler(async ({ data }) => {
    const { checkFormRateLimit, RATE_LIMIT_MESSAGE } = await import("./rate-limit.server");

    // Honeypot filled or submitted implausibly fast => silently accept, never store.
    const tooFast =
      typeof data.startedAt === "number" && Date.now() - data.startedAt < 2500;
    if ((data.website && data.website.trim() !== "") || tooFast) {
      return { success: true };
    }

    if (!(await checkFormRateLimit("waitlist", 5, 60))) {
      throw new Error(RATE_LIMIT_MESSAGE);
    }

    const { createClient } = await import("@supabase/supabase-js");
    const supabaseUrl = process.env["SUPABASE_URL"]!;
    const supabaseKey = process.env["SUPABASE_PUBLISHABLE_KEY"]!;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error("Server configuration error. Please try again later.");
    }

    const supabase = createClient(supabaseUrl, supabaseKey, {
      global: { fetch: createSupabaseFetch(supabaseKey) },
      auth: { autoRefreshToken: false, persistSession: false },
    });

    const row =
      data.kind === "school"
        ? ({
            full_name: data.fullName,
            email: data.email,
            portal_type: "school",
            submission_kind: "school_application",
            school_name: data.schoolName,
            school_location: data.schoolLocation,
            matric_learner_count: data.matricLearnerCount,
            biggest_challenge: data.biggestChallenge,
            biggest_challenge_other: data.biggestChallengeOther || null,
            contact_position: data.position,
            consent_given: true,
            phone: data.phone,
            message: data.message || null,
          } as const)
        : data.kind === "individual"
          ? ({
              full_name: data.fullName,
              email: data.email,
              portal_type: data.role,
              submission_kind: "individual_early_access",
              grade: data.grade,
              province: data.province,
              consent_given: true,
              phone: data.phone || null,
              message: data.message || null,
            } as const)
          : ({
            full_name: data.fullName,
            email: data.email,
            portal_type: data.role,
            submission_kind: "school_nomination",
            school_name: data.schoolName,
            province: data.province,
            phone: data.phone || null,
            message: data.message || null,
          } as const);

    const { error } = await supabase.from("waitlist_submissions").insert([row]);

    if (error) {
      console.error("Waitlist insert error:", error);
      throw new Error("Failed to submit your interest. Please try again later.");
    }

    return { success: true };
  });
